import { create } from "zustand";
import { publicFetcher } from "../utils/publicFetcher";
import type {
  EnumStoreState,
  SkillCategory,
  InterestCategory,
  EventType,
  CancellationReason,
  Skill,
  Interest,
} from "../types/enums";

export const useEnumStore = create<EnumStoreState>((set, get) => ({
  // State
  skills: [],
  interests: [],
  eventTypes: [],
  cancellationReasons: [],
  loading: false,
  error: null,

  // Actions
  setSkills: (skills: SkillCategory[]) => set({ skills }),
  setInterests: (interests: InterestCategory[]) => set({ interests }),
  setEventTypes: (eventTypes: EventType[]) => set({ eventTypes }),
  setCancellationReasons: (cancellationReasons: CancellationReason[]) =>
    set({ cancellationReasons }),
  setLoading: (loading: boolean) => set({ loading }),
  setError: (error: string | null) => set({ error }),

  // Fetch skills
  fetchSkills: async () => {
    try {
      set({ loading: true, error: null });
      const data = await publicFetcher<SkillCategory[]>({
        url: "/enums/skills",
      });
      set({ skills: data, loading: false });
    } catch (error) {
      set({
        error: "errors.skillsFailed",
        loading: false,
      });
      console.error("Error fetching skills:", error);
    }
  },

  // Fetch interests
  fetchInterests: async () => {
    try {
      set({ loading: true, error: null });
      const data = await publicFetcher<InterestCategory[]>({
        url: "/enums/interests",
      });
      set({ interests: data, loading: false });
    } catch (error) {
      set({
        error: "errors.interestsFailed",
        loading: false,
      });
      console.error("Error fetching interests:", error);
    }
  },

  // Fetch event types
  fetchEventTypes: async () => {
    try {
      set({ loading: true, error: null });
      const data = await publicFetcher<EventType[]>({
        url: "/enums/event_types",
      });
      set({ eventTypes: data, loading: false });
    } catch (error) {
      set({
        error: "errors.eventTypesFailed",
        loading: false,
      });
      console.error("Error fetching event types:", error);
    }
  },

  // Fetch cancellation reasons
  fetchCancellationReasons: async () => {
    try {
      set({ loading: true, error: null });
      const data = await publicFetcher<CancellationReason[]>({
        url: "/enums/reason_of_cancellation",
      });
      set({ cancellationReasons: data, loading: false });
    } catch (error) {
      set({
        error: "errors.cancellationReasonsFailed",
        loading: false,
      });
      console.error("Error fetching cancellation reasons:", error);
    }
  },

  // Fetch all enums at once
  fetchAllEnums: async () => {
    set({ loading: true, error: null });
    await Promise.all([
      get().fetchSkills(),
      get().fetchInterests(),
      get().fetchEventTypes(),
      get().fetchCancellationReasons(),
    ]);
  },

  // Getters/Selectors
  getSkillById: (id: number | string): Skill | SkillCategory | null => {
    const { skills } = get();
    // First, try to find as a category
    const category = skills.find((cat) => String(cat.id) === String(id));
    if (category) return category;

    // If not found as category, search in sub-items
    for (const group of skills) {
      if (group.skills) {
        const found = group.skills.find(
          (item) => String(item.skill_id) === String(id),
        );
        if (found) return found;
      }
    }
    return null;
  },

  getInterestById: (
    id: number | string,
  ): Interest | InterestCategory | null => {
    const { interests } = get();
    // First, try to find as a category
    const category = interests.find((cat) => String(cat.id) === String(id));
    if (category) return category;

    // If not found as category, search in sub-items
    for (const group of interests) {
      if (group.interests) {
        const found = group.interests.find(
          (item) => String(item.interest_id) === String(id),
        );
        if (found) return found;
      }
    }
    return null;
  },

  getCategoryById: (
    type: "interests" | "skills",
    id: number | string,
  ): SkillCategory | InterestCategory | null => {
    const data = type === "interests" ? get().interests : get().skills;
    return data.find((category) => String(category.id) === String(id)) || null;
  },

  getCancellationReasonById: (id: string): CancellationReason | null => {
    const { cancellationReasons } = get();
    return cancellationReasons.find((reason) => reason.id === id) || null;
  },
}));
